import { FC } from 'react';
import { css } from '@emotion/css';
import Link from 'next/link';

import CmsImage from 'components/CmsImage';

import colors from '@prezzee/ribbon-ui/lib/theme/foundations/colors';
import breakpoints from '@prezzee/ribbon-ui/lib/theme/foundations/breakpoints';
import {
  tabletSm,
  belowLargeScreen,
  smallScreenOrMobile,
  tabletOrMobile,
} from '@prezzee/ribbon/src/theme/Theme__Responsive.bs.js';
import { make as Button } from '@prezzee/ribbon/src/components/personal/Button/Button.bs.js';

import { t as SmartCardHeroBlock_t } from 'api/graphql/SmartCardHeroBlock/Gql__SmartCardHeroBlock__Decoder.gen';

const style = {
  container: css`
    background-color: ${colors.lilac[50]};
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0 0 44px;

    @media ${tabletSm} {
      align-items: center;
      flex-direction: column-reverse;
      padding: 0 16px 16px;
    }
  `,
  content: css`
    width: 60%;
    padding: 0 0 0 57px;

    @media (max-width: ${breakpoints.lg}) {
      width: 54%;
      padding: 41px 0 32px 32px;
    }

    @media ${tabletSm} {
      width: 100%;
      padding: 0;
    }
  `,
  title: css`
    font-size: 27px;
    line-height: 32px;
    font-weight: 600;
  `,
  description: css`
    margin-top: 12px;
  `,
  primaryBtn: css`
    margin: 0 12px 0 0;

    @media ${tabletSm} {
      margin: 0 0 12px 0;
    }
  `,
  buttonContainer: css`
    display: flex;
    margin-top: 24px;
    @media ${tabletSm} {
      flex-flow: column;

      button {
        width: 100%;
      }
    }
  `,
  image: css`
    width: 35%;

    @media ${tabletSm} {
      display: none;
    }
  `,
  mobileImage: css`
    display: none;
    width: 100%;

    @media ${tabletSm} {
      display: block;
    }
  `,
};

type Props = {
  block: SmartCardHeroBlock_t;
};

const SmartCardHeroSection: FC<Props> = ({
  block: { title, description, primaryButton, secondaryButton, image, mobileImage },
}) => {
  return (
    <div className={`${style.container} mx-4`}>
      <div className={style.content}>
        <h2 className={style.title}>{title}</h2>
        <p className={style.description}>{description}</p>
        <div className={style.buttonContainer}>
          <Button
            variant={primaryButton.variant}
            colour={primaryButton.colour}
            fill={primaryButton.fill}
            className={style.primaryBtn}
          >
            <Link href={primaryButton.cta_url} passHref>
              <a>
                <button>{primaryButton.cta_text}</button>
              </a>
            </Link>
          </Button>
          <Button variant={secondaryButton.variant} colour={secondaryButton.colour} fill={secondaryButton.fill}>
            <Link href={secondaryButton.cta_url} passHref>
              <a>
                <button>{secondaryButton.cta_text}</button>
              </a>
            </Link>
          </Button>
        </div>
      </div>
      <CmsImage
        src={image.url}
        alt={image.description}
        width={image.width}
        height={image.height}
        className={style.image}
        sizes={`${smallScreenOrMobile} 220px, ${tabletOrMobile} 260px, ${belowLargeScreen} 350px, 500px`}
      />
      <CmsImage
        src={mobileImage.url}
        alt={mobileImage.description}
        width={mobileImage.width}
        height={mobileImage.height}
        className={style.mobileImage}
        sizes={`48vw`}
      />
    </div>
  );
};

export default SmartCardHeroSection;
